import React from "react";
import { Link } from "react-router-dom";
import "../../styles/OPProtocols.css";
import { Helmet, HelmetProvider } from "react-helmet-async";

const OPProtocols = () => {
  return (
    <div className="OP-container">
      <HelmetProvider>
        <Helmet>
          <title>
            Outpatient Protocols for Respiratory Management in Neuromuscular
            Disease
          </title>
          <meta
            name="description"
            content="Comprehensive outpatient care protocols for patients at risk of respiratory failure due to neuromuscular conditions. Includes air stacking, assisted coughing, mechanical in-exsufflation, and nocturnal IPPV."
          />
          <meta
            name="keywords"
            content="outpatient respiratory protocols, neuromuscular disease, assisted coughing, air stacking, Cough Assist, IPPV, EtCO2 monitoring, SaO2 management, nocturnal ventilation, noninvasive respiratory support"
          />
          <meta name="author" content="Millennium Respiratory Care" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <meta
            property="og:image"
            content="https://doctorbach.com/images/BachJohn.jpg"
          />
          <meta
            property="og:image"
            content="https://doctorbach.com/favicon.png"
          />
          <meta
            property="og:url"
            content="https://doctorbach.com/resources/op"
          />
          <meta property="og:type" content="article" />
        </Helmet>
      </HelmetProvider>
      <div className="precautions">
        <h3>Outpatient Protocols</h3>
        <div className="er-pro">
          <p>
            Patients are considered to be at risk for respiratory failure
            particularly during chest colds when they have assisted PCF below
            270 liters per minute. They are prescribed oximeters and trained in
            air stacking insufflated volumes via mouth and nasal interfaces,
            manually assisted coughing, mechanical in-exsufflation (MI-E) at
            [+35 to +50] to [-35 to -50] cm H<sub>2</sub>O pressure drops with
            abdominal thrusts applied during exsufflations, and given rapid
            (less than 1 hour) access to a portable volume ventilator, a Cough
            Assist (J. H. Emerson Co., Cambridge, MA), and to various
            mouthpieces and nasal interfaces.
          </p>
          <p>
            The patients and care providers are instructed that any decreases in
            SaO<sub>2</sub> below 95% indicate either hypoventilation or the
            presence of airway mucus accumulation that must be cleared to
            prevent atelectasis and pneumonia. They are told to use
            oxyhemoglobin saturation (SaO<sub>2</sub>) monitoring whenever
            fatigued, short of breath, or ill. They use noninvasive IPPV and
            manually and mechanically assisted coughing as needed to maintain
            normal SaO<sub>2</sub> at all times.
          </p>
          <p>
            Patients with elevated end-tidal blood carbon dioxide (EtCO
            <sub>2</sub>) levels or periods of daytime SaO<sub>2</sub> below 95%
            undergo nocturnal SaO<sub>2</sub> monitoring. When symptomatic or
            nocturnal means are below 94%, a trial of nocturnal nasal IPPV is
            provided. People continue to use nocturnal nasal IPPV when they felt
            less fatigue and nocturnal mean SaO<sub>2</sub> increases. Most
            young patients use noninvasive IPPV for the first time to assist
            lung ventilation during chest infections.
          </p>
        </div>

        <ul>
          <li>
            Patients at risk
            <ul>
              <li>During chest colds w/ assisted PCF below 270 LPM</li>
            </ul>
          </li>

          <li>
            Patients prescribed
            <ul>
              <li>Oximeter</li>
            </ul>
          </li>

          <li>
            Patients trained in
            <ul>
              <li>
                Air stacking insufflated volumes via mouth and nasal interfaces
              </li>
              <li>Manually assisted coughing</li>
              <li>
                Mechanical in-exsufflation at [+35 to +50] to [-35 to -50] cm H
                <sub>2</sub>O
              </li>
            </ul>
          </li>

          <li>
            Patients given 1-hour access to
            <ul>
              <li>Portable volume ventilator</li>
              <li>Cough Assist (J. H. Emerson Co., Cambridge, MA)</li>
              <li>Various mouthpieces and nasal interfaces</li>
            </ul>
          </li>

          <li>
            Patients and care providers are instructed
            <ul>
              <li>
                SaO<sub>2</sub> &lt;95% indicates hypoventilation or airway
                mucus accumulation that must be cleared to prevent atelectasis
                and pneumonia
              </li>
              <li>
                Use SaO<sub>2</sub> monitoring whenever fatigued, short of
                breath, or ill
              </li>
              <li>
                Use noninvasive IPPV and manually and mechanically assisted
                coughing as needed to maintain normal SaO<sub>2</sub> at all
                times
              </li>
            </ul>
          </li>

          <li>
            Patients with elevated EtCO<sub>2</sub> or daytime SaO<sub>2</sub>{" "}
            &lt;95%
            <ul>
              <li>
                Undergo nocturnal SaO<sub>2</sub> monitoring
              </li>
              <li>
                When symptomatic or nocturnal SaO<sub>2</sub> mean &lt;94%
              </li>
              <li>A trial of nocturnal nasal IPPV is provided</li>
            </ul>
          </li>
        </ul>

        <div className="er-pro">
          <p>
            People continue to use nocturnal nasal IPPV when they felt less
            fatigue and nocturnal mean SaO<sub>2</sub> increases.
          </p>
          <p>
            Most young patients use noninvasive IPPV for the first time to
            assist lung ventilation during chest infections.
          </p>
        </div>

        <div className="er-pro">
          <h4>Additional information</h4>
          <p>
            For more information, click here for{" "}
            <Link
              to="/resources/icu"
              style={{ textDecoration: "none", color: "darkblue" }}
            >
              Intensive Care Protocols
            </Link>{" "}
            and{" "}
            <Link
              to="/resources/er"
              style={{ textDecoration: "none", color: "darkblue" }}
            >
              emergency room precautions
            </Link>
            .
          </p>
          <p>
            For more information about Air Stacking or Glossopharyngeal
            Breathing (GPB),{" "}
            <Link
              to="/resources/as"
              style={{ textDecoration: "none", color: "darkblue" }}
            >
              click here
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
};

export default OPProtocols;
